import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { Playbook } from "@workspace/api-client-react";
import { cn } from "@/lib/utils";
import { PlaybookCardBody } from "@/components/playbook-card-body";
import { PlaybookTitle } from "@/components/playbook-title";

// The scroller's own left padding (px-5 = 20px) is scroll-snap-aligned to on load
// in some browsers, resting at scrollLeft ~20 before the user has ever touched it —
// comfortably clear of that so the left arrow never appears on a lane nobody has
// scrolled yet, while a real scroll (a card-width or more) still crosses it easily.
const SCROLL_EPSILON = 32;

/**
 * A horizontally-scrolling "swim lane" of playbook cards — a titled row with an
 * optional "View all" action and snap scrolling, so the next card peeks.
 *
 * Used for mobile browsing on the dashboard and library page. Renders nothing
 * when there are no playbooks so empty lanes disappear.
 */
export function PlaybookSwimLane({
  title,
  playbooks,
  onCardClick,
  onViewAll,
  selectedId,
  className,
}: {
  title?: string;
  playbooks: Playbook[];
  onCardClick: (playbook: Playbook, index: number) => void;
  onViewAll?: () => void;
  selectedId?: string | null;
  className?: string;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollAffordance = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > SCROLL_EPSILON);
    setCanScrollRight(el.scrollLeft < maxScroll - SCROLL_EPSILON);
  }, []);

  // Re-check after the row's actual content (thumbnails, fonts) settles, and on resize —
  // a lane that starts too short to scroll must still pick up the right arrow if the
  // viewport shrinks, and drop it again if it grows back.
  useEffect(() => {
    updateScrollAffordance();
    window.addEventListener("resize", updateScrollAffordance);
    return () => window.removeEventListener("resize", updateScrollAffordance);
  }, [updateScrollAffordance, playbooks.length]);

  const scrollByPage = (direction: 1 | -1) => {
    scrollerRef.current?.scrollBy({ left: direction * scrollerRef.current.clientWidth * 0.8, behavior: "smooth" });
  };

  if (playbooks.length === 0) return null;

  return (
    <section className={cn("mb-8", className)}>
      <div className={cn("flex items-center justify-between gap-3 px-0.5", (title || onViewAll) ? "mb-3" : "")}>
        {title && <h3 className="font-display text-lg font-medium tracking-[-0.015em] text-foreground">{title}</h3>}
        {onViewAll && (
          <button
            type="button"
            onClick={onViewAll}
            className="shrink-0 text-xs font-semibold text-primary hover:underline"
          >
            View all
          </button>
        )}
      </div>

      {/* Hover zone for the Netflix-style edge arrows — a plain (unnamed) `group` on
          each card button already exists for its own internal hover state, so this
          row-level trigger is named to keep the two from ever colliding. */}
      <div className="relative group/lane">
        {/* Edge-to-edge horizontal scroller (cancels the parent's px-5). */}
        <div
          ref={scrollerRef}
          onScroll={updateScrollAffordance}
          className="flex gap-3 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-5 px-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {playbooks.map((playbook, idx) => {
            const isActive = selectedId === playbook.id;
            return (
              <div key={playbook.id} className="shrink-0 snap-start w-[66%] max-w-[248px] md:w-[248px] md:max-w-none">
                <button
                  type="button"
                  onClick={() => onCardClick(playbook, idx)}
                  className={cn(
                    // Lilac shows only while the photo loads (or if it fails).
                    "group relative w-full aspect-[4/5] rounded-3xl overflow-hidden flex flex-col gap-4 p-6 text-left bg-lilac transition-transform hover:-translate-y-1 hover:shadow-lg active:scale-[0.98]",
                    isActive && "ring-2 ring-primary/50",
                    playbook.isCustom && "opacity-90",
                  )}
                >
                  <PlaybookCardBody playbook={playbook} isActive={isActive} hideTitle />
                </button>
                {/* Title below the card */}
                <PlaybookTitle
                  title={playbook.title}
                  variant="card"
                  className="mt-2.5 px-1 text-foreground"
                />
              </div>
            );
          })}
        </div>

        {canScrollLeft && (
          <button
            type="button"
            onClick={() => scrollByPage(-1)}
            aria-label="Scroll left"
            className="absolute inset-y-0 left-0 z-10 flex w-14 items-center justify-start bg-gradient-to-r from-background via-background/70 to-transparent pl-1 opacity-0 pointer-events-none transition-opacity duration-200 group-hover/lane:opacity-100 group-hover/lane:pointer-events-auto"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-foreground/70 shadow-md ring-1 ring-black/5 transition-colors hover:text-foreground">
              <ChevronLeft className="h-5 w-5" />
            </span>
          </button>
        )}

        {canScrollRight && (
          <button
            type="button"
            onClick={() => scrollByPage(1)}
            aria-label="Scroll right"
            className="absolute inset-y-0 right-0 z-10 flex w-14 items-center justify-end bg-gradient-to-l from-background via-background/70 to-transparent pr-1 opacity-0 pointer-events-none transition-opacity duration-200 group-hover/lane:opacity-100 group-hover/lane:pointer-events-auto"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-foreground/70 shadow-md ring-1 ring-black/5 transition-colors hover:text-foreground">
              <ChevronRight className="h-5 w-5" />
            </span>
          </button>
        )}
      </div>
    </section>
  );
}
